import type { ReactNode } from "react";

import { cn } from "@homeinstead/ui";
import { cva } from "class-variance-authority";

type Alignment = "left" | "center" | "right";
type ContentAlignmentPreset = Alignment | "responsive-medium-center";

type ContentAlignmentCustom = {
  mobile: Alignment;
  tablet?: Alignment;
  desktop?: Alignment;
};

type ContentAlignment = ContentAlignmentPreset | ContentAlignmentCustom;
type VerticalPadding = "default" | "slim" | "none";

type LeadFormStepProps = {
  contentAlignment?: ContentAlignment;
  verticalPadding?: VerticalPadding;
  children?: ReactNode;
  className?: string;
  isVisible?: boolean;
};

const TEXT_ALIGNMENT: Record<Alignment, string> = {
  center: "text-center",
  left: "text-left",
  right: "text-right",
};

const PRESET_CLASSES: Record<ContentAlignmentPreset, string> = {
  ...TEXT_ALIGNMENT,
  "responsive-medium-center": "text-left md:text-center xl:text-left",
};

function resolveAlignmentClasses(alignment: ContentAlignment = "left"): string {
  if (typeof alignment === "string") {
    return PRESET_CLASSES[alignment];
  }
  const { mobile, tablet, desktop } = alignment;
  const parts = [TEXT_ALIGNMENT[mobile]];
  if (tablet) {
    parts.push(`md:${TEXT_ALIGNMENT[tablet]}`);
  }
  if (desktop) {
    parts.push(`xl:${TEXT_ALIGNMENT[desktop]}`);
  }
  return parts.join(" ");
}

// Below xl the step leaves the grid. Inside a button-stack form the same
// thing happens below md, and from md the step stays put but invisible.
const INACTIVE_STEP_CLASS =
  "max-xl:hidden xl:pointer-events-none xl:invisible xl:select-none xl:overflow-hidden group-data-[layout=buttonStack]/lead-form:max-md:hidden group-data-[layout=buttonStack]/lead-form:md:pointer-events-none group-data-[layout=buttonStack]/lead-form:md:invisible group-data-[layout=buttonStack]/lead-form:md:select-none group-data-[layout=buttonStack]/lead-form:md:overflow-hidden";

const paddingVariants = cva("", {
  defaultVariants: {
    verticalPadding: "default",
  },
  variants: {
    verticalPadding: {
      default: "py-10 lg:py-20",
      none: "py-0",
      slim: "py-6",
    },
  },
});

function LeadFormStep({
  contentAlignment,
  verticalPadding,
  children,
  className,
  isVisible = true,
}: LeadFormStepProps) {
  return (
    <div
      aria-hidden={!isVisible}
      className={cn(
        resolveAlignmentClasses(contentAlignment),
        paddingVariants({ verticalPadding }),
        className,
        // Below the layout breakpoint, display:none drops the step so the
        // grid height tracks only the active step — a tall step-1 hero would
        // otherwise leave a gap. At the breakpoint and up, visibility:hidden
        // keeps the step in the grid so the container stays tall enough for
        // every step. overflow-hidden clips the step-1 hero image so it
        // cannot paint past the viewport once a later step is active. The
        // max-* class is breakpoint-scoped and does not conflict with "flex"
        // in tailwind-merge.
        !isVisible && INACTIVE_STEP_CLASS,
      )}
    >
      {children}
    </div>
  );
}

export type {
  Alignment,
  ContentAlignment,
  ContentAlignmentCustom,
  ContentAlignmentPreset,
  LeadFormStepProps,
  VerticalPadding,
};

export { LeadFormStep };
